import { deleteRequestWithBody, getRequest, postRequest, putRequest } from '../../../core/utils/api';
import { type KeycloakContext } from '../../authentication/keycloak/KeycloakProvider';
import type DashboardOffice from '../../dashboard/models/dashboard-office';
import type Office from '../models/office';
import showStatusCodeError from './logging.service';
import { dataCache, OFFICES_CACHE_KEY } from '../../../core/utils/data-cache';

export const createOffice = async (office: Office, keycloak: KeycloakContext): Promise<Office | undefined> => {
  const apiResponse = await postRequest<Office>('office/add-office-information', keycloak, office);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  // Invalidate the cached offices list so the next visit fetches fresh data.
  dataCache.invalidate(OFFICES_CACHE_KEY);
  return apiResponse.json;
};

export const updateOffice = async (office: Office, keycloak: KeycloakContext): Promise<Office | undefined> => {
  const apiResponse = await putRequest<Office>('office/update-office-information', keycloak, office);
  if (apiResponse.error) {
    showStatusCodeError(apiResponse.status);
    return undefined;
  }

  // Invalidate the cached offices list to ensure updated data is reflected.
  dataCache.invalidate(OFFICES_CACHE_KEY);
  return apiResponse.json;
};

export const getAllDashboardOffices = async (
  keycloak: KeycloakContext,
  pageNumber: number,
  pageSize: number,
): Promise<DashboardOffice[] | undefined> => {
  const apiResponse = await getRequest<DashboardOffice[]>(`office/all?pageNumber=${pageNumber}&pageSize=${pageSize}`, keycloak);
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const getAllOfficesForAuthorEmail = async (keycloak: KeycloakContext): Promise<Office[] | undefined> => {
  const apiResponse = await getRequest<Office[]>('office/by-author-email', keycloak);
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const getAllOfficesForAdmin = async (keycloak: KeycloakContext): Promise<Office[] | undefined> => {
  const apiResponse = await getRequest<Office[]>('office/by-admin', keycloak);
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const getAllOfficesForOfficeManagerOrAdmin = async (keycloak: KeycloakContext): Promise<Office[] | undefined> => {
  const apiResponse = await getRequest<Office[]>('office/by-officemanager-or-admin', keycloak);
  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const getOfficeInformationById = async (id: string, keycloak: KeycloakContext): Promise<Office | undefined> => {
  const apiResponse = await getRequest<Office>(`office/office-information-by-office-id?officeId=${id}`, keycloak);

  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

export const getOfficeById = async (id: string, keycloak: KeycloakContext): Promise<Office | undefined> => {
  const apiResponse = await getRequest<Office>(`office?officeId=${id}`, keycloak);

  if (apiResponse.error) {
    return undefined;
  }

  return apiResponse.json;
};

/**
 * What deleting one or more offices would cancel and retire, counted by the server from today in each office's own
 * timezone - the same service the deletion runs, so the numbers are the deletion's own. `hasImpact` is the server's
 * answer to "does this need acknowledging", and decides whether the delete endpoint demands the flag.
 */
export interface RetirementImpact {
  readonly offices: number;
  readonly people: number;
  /** Half-day bookings from today onwards. An upper bound: a half-day already checked into is kept. */
  readonly bookings: number;
  readonly days: number;
  readonly groups: number;
  readonly groupReservations: number;
  readonly facilityReservations: number;
  /** Shared with a live office: they only lose the link. */
  readonly parkingsUnlinked: number;
  /** Used by no live office any more: retired. */
  readonly parkingsRetired: number;
  readonly facilitiesUnlinked: number;
  /** Used by no live office any more: removed together with their reservation history. */
  readonly facilitiesRetired: number;
  readonly hasImpact: boolean;
}

export interface OfficeDeletionImpact {
  readonly officeId: string;
  readonly officeName: string;
  readonly impact: RetirementImpact;
}

export const getOfficeDeletionImpact = async (
  officeId: string,
  keycloak: KeycloakContext,
): Promise<OfficeDeletionImpact | undefined> => {
  const apiResponse = await getRequest<OfficeDeletionImpact>(`office/${officeId}/deletion-impact`, keycloak);

  if (apiResponse.error) {
    // Deliberately silent: the dialog shows the failure itself, as the room deletion dialog does.
    return undefined;
  }

  return apiResponse.json;
};

/**
 * What a delete call came to. On failure, `errorKey` is the translation key of the one message to show - the caller
 * shows it, so a failure is reported exactly once and in the words of the deletion rather than a generic edit error.
 */
export interface DeletionOutcome {
  readonly deleted: boolean;
  readonly errorKey?: string;
}

/**
 * The message for a refused office or company deletion.
 *
 * 409 is the acknowledgement gate: something was booked, or a group or reservation created, while the dialog was
 * open, so what it showed is out of date. 404 is a deletion that had already happened - another administrator, or
 * the same click landing twice.
 */
export const deletionErrorKey = (status: number, fallbackKey: string): string => {
  switch (status) {
    case 401:
    case 403:
      return 'unauthorized-error';
    case 404:
      return 'deletion-not-found-error';
    case 409:
      return 'deletion-impact-changed-error';
    default:
      return fallbackKey;
  }
};

/**
 * Deletes an office, acknowledging what it cancels. The server refuses with 409 when the office still has future
 * bookings, groups or facility reservations, or would retire a parking or facility, and the flag is absent.
 */
export const deleteOfficeById = async (officeId: string, keycloak: KeycloakContext, acknowledged = false): Promise<DeletionOutcome> => {
  const apiResponse = await deleteRequestWithBody('office/delete-office-information', keycloak, { id: officeId, acknowledged });

  if (apiResponse.error) {
    return { deleted: false, errorKey: deletionErrorKey(apiResponse.status, 'office-delete-error-message') };
  }

  // Invalidate the cached offices list so the deleted office is no longer shown.
  dataCache.invalidate(OFFICES_CACHE_KEY);
  return { deleted: true };
};

export interface CateringLunchPreviewRow {
  datum: string;
  dag: string;
  lunch: number;
}

export const getCateringLunchPreview = async (
  officeId: string,
  keycloak: KeycloakContext,
  daysAhead?: number,
): Promise<CateringLunchPreviewRow[] | undefined> => {
  const daysParam = daysAhead != null ? `&daysAhead=${daysAhead}` : '';
  const url = `office/catering-lunch-preview?officeId=${officeId}${daysParam}`;
  const apiResponse = await getRequest<CateringLunchPreviewRow[]>(url, keycloak);
  if (apiResponse.error) {
    return undefined;
  }
  return apiResponse.json;
};

export const sendCateringTestEmail = async (
  officeId: string,
  keycloak: KeycloakContext,
): Promise<boolean> => {
  const apiResponse = await postRequest<void>(`office/send-catering-test-email?officeId=${officeId}`, keycloak, null);
  return !apiResponse.error;
};
